import { useRef } from 'react';
import { AnimatePresence, useInView } from 'framer-motion';
import { StaticImage } from 'gatsby-plugin-image';
import { rgba } from 'polished';
import styled from 'styled-components';

import { styles as textStyles } from '@/components/v2/UtilityCss/Text';
import { INTERNAL_LINKS } from '@/utils';

import { backgroundImageHatched, ContentMarginLine } from '../v2/GridLines';
import { LayoutGridCondensed } from '../v2/Page/LayoutGrid';

import { config } from './config';
import { useIncrementSelection, Workflow } from './Workflow';

const STATIC_IMG_PATH = '../../../static/images/home-2025';

const OPTIONS = [
  {
    value: 0,
    title: 'Natural language in, smart answers out',
    copy: (
      <Workflow.OptionContent>
        Ask questions in plain language. AI translates them into data queries,
        charts, and answers.
        <Workflow.OptionButton
          disableCornerLines
          link={INTERNAL_LINKS.Magic}
          text="Learn more about AI in Hex"
        />
      </Workflow.OptionContent>
    ),
    media: (
      <Workflow.IllustrationImg>
        <StaticImage
          alt="Prompt illustration"
          sizes="(min-width: 800px) 50vw, 100vw"
          src={`${STATIC_IMG_PATH}/self-serve/1.png`}
          transformOptions={{ fit: 'contain' }}
        />
      </Workflow.IllustrationImg>
    )
  },
  {
    value: 1,
    title: 'One source of truth. A thousand ways to use it',
    copy: (
      <Workflow.OptionContent>
        Explore governed, reusable semantic models to ensure consistency and
        trust across your org.
      </Workflow.OptionContent>
    ),
    media: (
      <Workflow.IllustrationImg data-full>
        <StaticImage
          alt="Semantic models illustration"
          sizes="(min-width: 800px) 50vw, 100vw"
          src={`${STATIC_IMG_PATH}/self-serve/2.png`}
          transformOptions={{ fit: 'contain' }}
        />
      </Workflow.IllustrationImg>
    )
  },
  {
    value: 2,
    title: 'Easy for business users, sustainable for the data team',
    copy: (
      <Workflow.OptionContent>
        Empower anyone to explore data with an interface designed for speed,
        clarity, and zero SQL.
        <Workflow.OptionButton
          disableCornerLines
          link={INTERNAL_LINKS.Explore}
          text="Learn more about Explore"
        />
      </Workflow.OptionContent>
    ),
    media: (
      <Workflow.IllustrationImg>
        <StaticImage
          alt="No code illustration"
          sizes="(min-width: 800px) 50vw, 100vw"
          src={`${STATIC_IMG_PATH}/self-serve/3.png`}
          transformOptions={{ fit: 'contain' }}
        />
      </Workflow.IllustrationImg>
    )
  }
];

const StyledContentMarginLine = styled(ContentMarginLine)`
  --border-color: ${({ theme }) => theme.palette.fg600};

  &:not(&[data-right]) {
    background-image: ${({ theme }) => backgroundImageHatched(theme)};
  }

  @media (max-width: ${({ theme }) => theme.mediaQuery.LARGE}) {
    display: none;
  }
`;

export function SelfServe() {
  const ref = useRef<HTMLFieldSetElement>(null);
  const isInView = useInView(ref, {
    margin: '-160px 0px 0px 0px',
    amount: 0.25
  });
  const { increment, option, value, set } = useIncrementSelection(OPTIONS);

  return (
    <Workflow.Section id={config.workflows.self_serve.anchor}>
      <LayoutGridCondensed>
        <StyledContentMarginLine />
        <Workflow.Content>
          <Workflow.TagLink
            className={textStyles.annotation}
            data-self-serve
            to={INTERNAL_LINKS.Explore}>
            {config.workflows.self_serve.icon} Discover Self-serve
          </Workflow.TagLink>
          <Workflow.Headline>
            Enable self-serve, without the silos
          </Workflow.Headline>
          <Workflow.SubHeadline>
            Provide a simple workflow for natural language driven insights in a
            beautiful, visual-based interface, while ensuring data stays
            reusable and trusted.
          </Workflow.SubHeadline>

          <Workflow.Selector
            ref={ref}
            color={config.workflows.self_serve.color}>
            {OPTIONS.map(opt => (
              <Workflow.Option
                key={opt.value}
                checked={value === opt.value}
                group={config.workflows.self_serve.anchor}
                isInView={isInView}
                title={opt.title}
                value={opt.value}
                onAnimationComplete={increment}
                onChange={() => {
                  set(opt.value);
                }}>
                {opt.copy}
              </Workflow.Option>
            ))}
          </Workflow.Selector>
        </Workflow.Content>
        <Workflow.IllustrationArea
          style={{
            '--workflow-border-color': rgba(
              config.workflows.self_serve.color,
              0.37
            )
          }}>
          <Workflow.IllustrationContainer>
            <AnimatePresence mode="popLayout">
              <Workflow.IllustrationPresenceAnim key={option.value}>
                {option.media}
              </Workflow.IllustrationPresenceAnim>
            </AnimatePresence>
          </Workflow.IllustrationContainer>
        </Workflow.IllustrationArea>
        <StyledContentMarginLine data-right />
      </LayoutGridCondensed>
    </Workflow.Section>
  );
}
